/*
 * Temporary Emergent-managed Google Auth bridge.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Removing this bridge = delete this file, revert the branch inside
 * AppAuthProvider.js, and flip REACT_APP_AUTH_MODE back to `oidc` (or `disabled`).
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import { LogIn, ShieldAlert } from "lucide-react";
import axios from "axios";
import { api, configureApiAuth } from "@/lib/api";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_STORAGE_KEY = "portcall.emergent.session_token";
const SESSION_EXCHANGE_TIMEOUT_MS = 15_000;

function LoadingScreen() {
  return (
    <div
      className="min-h-screen bg-[#050B14] text-slate-400 grid place-items-center font-mono text-xs"
      data-testid="emergent-auth-loading"
    >
      Establishing secure session…
    </div>
  );
}

function SignInScreen({ error, onLogin }) {
  return (
    <div className="min-h-screen bg-[#050B14] text-slate-100 grid place-items-center p-6">
      <div className="panel p-7 w-full max-w-md" data-testid="emergent-auth-signin">
        <div className="font-heading text-2xl font-black tracking-tight" data-testid="signin-brand">Portcall HQ</div>
        <p className="text-sm text-slate-400 mt-2">
          Sign in with your organisation Google account to continue.
        </p>
        {error && (
          <div className="mt-4 sev-critical p-3 text-sm flex gap-2" data-testid="emergent-auth-error">
            <ShieldAlert size={17} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <Button
          onClick={onLogin}
          className="mt-5 w-full bg-sky-600 hover:bg-sky-500 rounded-none"
          data-testid="emergent-auth-google-signin"
        >
          <LogIn size={15} className="mr-2" /> Sign in with Google
        </Button>
        <p className="text-xs text-slate-500 mt-4">
          Access is limited to allow-listed accounts.
        </p>
      </div>
    </div>
  );
}

function readSessionIdFromHash() {
  const hash = window.location.hash || "";
  if (!hash.includes("session_id=")) return null;
  const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  return params.get("session_id");
}

function startGoogleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function EmergentAuthBridge({ AuthContext, children }) {
  const [principal, setPrincipal] = useState(null);
  const [status, setStatus] = useState("checking"); // checking | signedout | ready | error
  const [error, setError] = useState("");
  const exchangePromiseRef = useRef(null);

  const clearSession = useCallback(() => {
    try { window.localStorage.removeItem(SESSION_STORAGE_KEY); } catch (_) { /* ignore */ }
    setPrincipal(null);
  }, []);

  const currentToken = useCallback(
    () => { try { return window.localStorage.getItem(SESSION_STORAGE_KEY) || null; } catch (_) { return null; } },
    [],
  );

  const login = useCallback(() => startGoogleLogin(), []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/emergent-auth/logout`, null, {
        headers: { Authorization: `Bearer ${currentToken() || ""}` },
      });
    } catch (_) { /* best-effort */ }
    clearSession();
    setStatus("signedout");
  }, [clearSession, currentToken]);

  // Configure the shared axios client to send Bearer + cookies.
  useEffect(() => {
    configureApiAuth({
      getAccessToken: async () => currentToken(),
      onUnauthorized: () => {
        clearSession();
        setStatus("signedout");
      },
    });
    return () => configureApiAuth();
  }, [clearSession, currentToken]);

  // Startup: (a) exchange session_id if present, else (b) probe existing session.
  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const sessionId = readSessionIdFromHash();
      if (sessionId) {
        try {
          // React Strict Mode starts, cleans up, and starts effects again. Reuse
          // one exchange promise so the active effect can finish the callback
          // without attempting to consume the one-time session_id twice.
          if (!exchangePromiseRef.current) {
            exchangePromiseRef.current = axios.post(
              `${BACKEND_URL}/api/emergent-auth/session`,
              { session_id: sessionId },
              { timeout: SESSION_EXCHANGE_TIMEOUT_MS },
            );
          }
          const resp = await exchangePromiseRef.current;
          const token = resp.data?.session_token;
          const resolvedPrincipal = resp.data?.principal;
          if (!token || !resolvedPrincipal) {
            throw new Error("Identity provider returned incomplete session data.");
          }
          try { window.localStorage.setItem(SESSION_STORAGE_KEY, token); } catch (_) { /* ignore */ }
          // Scrub the fragment before anything else observes it.
          window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
          if (!cancelled) {
            setPrincipal(resolvedPrincipal);
            setStatus("ready");
          }
          return;
        } catch (err) {
          if (!cancelled) {
            const detail = err?.response?.data?.detail;
            const isTimeout = err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "");
            setError(
              detail
              || (isTimeout ? "Sign-in is taking longer than expected. Please try again." : null)
              || err?.message
              || "Sign-in was rejected. Contact an admin to be added to the allow-list.",
            );
            setStatus("error");
            clearSession();
          }
          window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
          return;
        }
      }

      // No new session_id: probe existing session (cookie or bearer).
      try {
        const me = await api.me();
        if (!cancelled) {
          setPrincipal(me);
          setStatus("ready");
        }
      } catch (_) {
        if (!cancelled) {
          clearSession();
          setStatus("signedout");
        }
      }
    }

    bootstrap();
    return () => { cancelled = true; };
  }, [clearSession]);

  const value = useMemo(() => ({
    isAuthenticated: status === "ready" && Boolean(principal),
    isLoading: status === "checking",
    principal,
    capabilities: principal?.capabilities || [],
    hasCapability: capability => Boolean(principal?.capabilities?.includes(capability)),
    getAccessToken: async () => currentToken(),
    login,
    logout,
  }), [status, principal, login, logout, currentToken]);

  if (status === "checking") return <LoadingScreen />;
  if (status === "error") return <SignInScreen error={error} onLogin={login} />;
  if (status === "signedout" || !principal) return <SignInScreen onLogin={login} />;
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
